import { useMemo } from 'react';
import type { NoteFieldSaveOverride } from '../NoteField';

/** Params for {@link useParentLinkedNoteSaveOverride}. */
export interface UseParentLinkedNoteSaveOverrideParams {
  /** When false, NoteField uses its default persistence path. */
  enabled: boolean;
  /** Persists note text through the parent entity API (e.g. PATCH visit with `{ note }`). */
  onSaveNote: (text: string) => void;
  isSaving: boolean;
  error?: unknown;
}

/**
 * Builds a {@link NoteField} `save` override so debounced autosave PATCHes the
 * parent entity (backend `syncItemNote` dual-write) instead of `/notes` directly.
 *
 * @param params - Whether the override is active and parent save wiring.
 * @returns Save override for edit flows, or undefined when disabled.
 */
export function useParentLinkedNoteSaveOverride({
  enabled,
  onSaveNote,
  isSaving,
  error,
}: UseParentLinkedNoteSaveOverrideParams): NoteFieldSaveOverride | undefined {
  return useMemo(() => {
    if (!enabled) {
      return undefined;
    }
    return {
      onSave: onSaveNote,
      isSaving,
      error,
    };
  }, [enabled, onSaveNote, isSaving, error]);
}
